import type { Project } from "../types";
import { devLog } from '../lib/devLog';
import { isRegistryPlaceholderTitle } from "../utils/projectName";
import { resolveProjectDisplayName } from "./embedRegistryProjectList";
import { sanitizeProjectForEmbed } from "./embedLocalWorkspacePolicy";
import {
  TEAMVER_BFF_REQUEST_OPTIONS,
  fetchDesignAuthSession,
  getDesignBffClient,
  isDesignAuthRefreshDeclined,
  shouldSkipTeamverBffAuthCalls,
  withDesignBffCookieAuthRecovery,
  type DesignAuthSession,
} from "./designBffClient";
import { isTeamverEmbedMode } from "./designApiBase";
import { readTeamverViteEnv } from "./teamverViteEnv";
import { resolveActiveTeamverWorkspaceId } from "./activeTeamverWorkspace";
import { fetchTeamverDaemon } from "./teamverDaemonHeaders";
import { waitForTeamverEmbedBoot } from "./teamverEmbedBoot";
import { handleEmbedPassiveUnauthorized } from "./teamverEmbedPassiveAuth";
import { isTeamverProjectCollectionRouteSlug } from "./teamverProjectCollectionRouteSlugs";
import {
  clearTeamverProjectS3Prefix,
  clearAllTeamverProjectS3PrefixCache,
  readTeamverProjectS3Prefix,
  rememberTeamverProjectS3Prefix,
} from "./teamverProjectS3PrefixCache";
import { isTeamverProjectDeletedTombstoned } from "./deletedProjectTombstones";

/** SDK maps 403→ForbiddenError, 404→NotFoundError (not NetworkError). */
function httpStatusOf(err: unknown): number | null {
  if (!(err instanceof Error)) return null;
  const status = Number((err as { status?: unknown }).status);
  return Number.isFinite(status) && status > 0 ? status : null;
}

async function waitForEmbedBootIfNeeded(): Promise<void> {
  if (!isTeamverEmbedMode()) return;
  await waitForTeamverEmbedBoot();
}

export type TeamverProjectRegistryPayload = {
  odProjectId: string;
  title?: string;
  reactivateIfDeleted?: boolean;
};

export type TeamverRegisteredProject = {
  id?: string;
  odProjectId?: string;
  s3Prefix?: string;
  title?: string;
  ownerUserId?: string;
  status?: string;
  createdAt?: string | number;
  updatedAt?: string | number;
};

export class TeamverProjectRegistryError extends Error {
  readonly code: string;

  constructor(code: string) {
    super(code);
    this.name = "TeamverProjectRegistryError";
    this.code = code;
  }
}

const REGISTRY_ERROR_MESSAGES: Record<string, string> = {
  teamver_project_registry_unavailable:
    "teamver Slide 연동을 사용할 수 없습니다. 페이지를 새로고침한 뒤 다시 시도하세요.",
  teamver_workspace_required:
    "워크스페이스를 선택한 뒤 프로젝트를 만들어 주세요.",
  teamver_project_registry_sync_failed:
    "프로젝트를 워크스페이스 저장소에 등록하지 못했습니다. 잠시 후 다시 시도하세요.",
  teamver_project_registry_list_failed:
    "프로젝트 목록을 불러오지 못했습니다. 잠시 후 다시 시도하거나 페이지를 새로고침하세요.",
  teamver_project_s3_prefix_required:
    "프로젝트 저장소를 준비하지 못했습니다. 잠시 후 다시 시도하거나 페이지를 새로고침하세요.",
};

/** Embed create — registry hard-fail 사용자 메시지. */
export function formatTeamverProjectRegistryErrorMessage(
  code: string,
  fallback = "프로젝트를 만들 수 없습니다. 잠시 후 다시 시도하세요.",
): string {
  const key = code.trim();
  return REGISTRY_ERROR_MESSAGES[key] ?? fallback;
}

/** Embed — registry membership gate 거부 시 사용자 메시지. */
export function formatTeamverProjectAccessDeniedMessage(): string {
  return "이 워크스페이스에서 해당 프로젝트에 접근할 수 없습니다.";
}

/** Embed — deep-link hydration 실패(삭제·미등록) 시 사용자 메시지. */
export function formatTeamverProjectNotFoundMessage(): string {
  return "프로젝트를 찾을 수 없거나 삭제되었습니다.";
}

const FE_ACCESS_CACHE_MS = 30_000;
const REGISTRY_LIST_CACHE_MS = 15_000;
const SYNC_ALL_MIN_INTERVAL_MS = 60_000;
const REGISTRY_CREATE_RETRY_DELAYS_MS = [500, 1_500] as const;
const PROJECT_ACCESS_RETRY_DELAYS_MS = [0, 400, 800] as const;

type ProjectAccessCheckResult = "allowed" | "denied" | "transient";

function legacyRegistryMigrationEnabled(): boolean {
  return readTeamverViteEnv("VITE_TEAMVER_LEGACY_REGISTRY_SYNC") === "1";
}

const feAccessCache = new Map<string, { allowed: boolean; at: number }>();
let registryListCache: {
  workspaceId: string;
  userId: string;
  projects: TeamverRegisteredProject[];
  ids: Set<string>;
  at: number;
} | null = null;
/** Bumped on list invalidate so in-flight GETs cannot repopulate a stale membership. */
let registryListFetchEpoch = 0;
let registryListInflight: Promise<{
  workspaceId: string;
  userId: string | null;
  projects: TeamverRegisteredProject[];
} | null> | null = null;
let syncAllInflight: Promise<void> | null = null;
let syncAllAt = 0;
/** Workspace id of the last completed (or in-flight) syncAll run. */
let syncAllWorkspaceId: string | null = null;

/** Embed list gates — wait for in-flight legacy registry sync before filtering. */
export async function waitForTeamverRegistrySyncIfNeeded(): Promise<void> {
  if (!isTeamverEmbedMode()) return;
  if (syncAllInflight) {
    await syncAllInflight;
  }
}

function readRegistryOdProjectId(project: TeamverRegisteredProject): string | undefined {
  const id = project.odProjectId?.trim();
  return id || undefined;
}

function readSessionUserId(session: DesignAuthSession): string | null {
  return session.user?.userId?.trim() || null;
}

async function resolveRegistryUserId(): Promise<string | null> {
  if (shouldSkipTeamverBffAuthCalls() || isDesignAuthRefreshDeclined()) return null;
  try {
    const session = await fetchDesignAuthSession();
    if (!session?.authenticated) return null;
    return readSessionUserId(session);
  } catch {
    return null;
  }
}

function isTeamverBffUnauthorizedError(err: unknown): boolean {
  return err instanceof Error && (err as { status?: unknown }).status === 401;
}

function feAccessCacheKey(workspaceId: string, projectId: string, userId: string): string {
  return `${userId.trim()}:${workspaceId.trim()}:${projectId.trim()}`;
}

function invalidateFeAccessCache(projectId: string, workspaceId?: string): void {
  const trimmed = projectId.trim();
  if (!trimmed) return;
  if (workspaceId?.trim()) {
    const suffix = `:${workspaceId.trim()}:${trimmed}`;
    for (const key of feAccessCache.keys()) {
      if (key.endsWith(suffix)) feAccessCache.delete(key);
    }
    clearTeamverProjectS3Prefix(trimmed, workspaceId.trim());
    return;
  }
  for (const key of feAccessCache.keys()) {
    if (key.endsWith(`:${trimmed}`)) feAccessCache.delete(key);
  }
  clearTeamverProjectS3Prefix(trimmed);
}

function primeFeAccessAllowed(projectId: string, workspaceId: string, userId: string): void {
  const trimmedId = projectId.trim();
  const trimmedWorkspaceId = workspaceId.trim();
  const trimmedUserId = userId.trim();
  if (!trimmedId || !trimmedWorkspaceId || !trimmedUserId) return;
  feAccessCache.set(feAccessCacheKey(trimmedWorkspaceId, trimmedId, trimmedUserId), {
    allowed: true,
    at: Date.now(),
  });
}

function invalidateRegisteredIdsCache(): void {
  registryListCache = null;
  registryListFetchEpoch += 1;
  // Do not clear registryListInflight — epoch already discards stale seeds;
  // waiters on the in-flight GET still share one network hop.
}

/** Last-good registry window for the active workspace (auth-blip fallback). */
function readCachedRegistryList(
  workspaceId: string,
  userId: string | null,
  options?: { ignoreTtl?: boolean },
): {
  workspaceId: string;
  userId: string | null;
  projects: TeamverRegisteredProject[];
} | null {
  if (!registryListCache || registryListCache.workspaceId !== workspaceId) return null;
  if (userId && registryListCache.userId !== userId) return null;
  if (!options?.ignoreTtl && Date.now() - registryListCache.at >= REGISTRY_LIST_CACHE_MS) {
    return null;
  }
  return {
    workspaceId,
    userId: userId ?? registryListCache.userId,
    projects: registryListCache.projects,
  };
}

/** Clear FE registry caches after auth/workspace changes. */
export function invalidateTeamverProjectRegistryCaches(): void {
  feAccessCache.clear();
  invalidateRegisteredIdsCache();
  clearAllTeamverProjectS3PrefixCache();
}

async function fetchDaemonProjectsForRegistry(): Promise<Project[]> {
  try {
    const resp = await fetchTeamverDaemon("/api/projects");
    if (!resp.ok) return [];
    const json = (await resp.json()) as { projects?: Project[] };
    const workspaceId = (await resolveActiveTeamverWorkspaceId())?.trim() ?? null;
    return (json.projects ?? [])
      .filter((project) => !isTeamverProjectCollectionRouteSlug(project.id))
      .filter((project) => !isTeamverProjectDeletedTombstoned(project.id, workspaceId))
      .map((project) => sanitizeProjectForEmbed(project));
  } catch {
    return [];
  }
}

async function getRegisteredProjectIds(_workspaceId: string): Promise<Set<string> | null> {
  return listTeamverRegisteredProjectIds();
}

/** @internal vitest only — module-level caches are not request-scoped. */
export function resetTeamverProjectRegistryStateForTests(): void {
  invalidateTeamverProjectRegistryCaches();
  registryListInflight = null;
  syncAllInflight = null;
  syncAllAt = 0;
  syncAllWorkspaceId = null;
}

export function buildTeamverProjectRegistryPayload(
  project: Pick<Project, "id" | "name">,
  options?: { reactivateIfDeleted?: boolean },
): TeamverProjectRegistryPayload {
  const rawName = project.name?.trim();
  const title =
    rawName && !isRegistryPlaceholderTitle({ id: project.id, name: rawName })
      ? rawName
      : "";
  return {
    odProjectId: project.id,
    ...(title ? { title } : {}),
    reactivateIfDeleted: options?.reactivateIfDeleted === true,
  };
}

async function rememberRegistryS3Prefix(
  projectId: string,
  workspaceId: string,
  registered?: TeamverRegisteredProject | null,
): Promise<void> {
  const direct = registered?.s3Prefix?.trim();
  if (direct) {
    rememberTeamverProjectS3Prefix(workspaceId, projectId, direct);
    return;
  }
  const fetched = await fetchTeamverProject(projectId);
  rememberTeamverProjectS3Prefix(workspaceId, projectId, fetched?.s3Prefix);
}

function assertRegistryS3PrefixCached(workspaceId: string, projectId: string): void {
  if (!readTeamverProjectS3Prefix(workspaceId, projectId)?.trim()) {
    throw new TeamverProjectRegistryError("teamver_project_s3_prefix_required");
  }
}

export async function registerTeamverProjectIfNeeded(
  project: Pick<Project, "id" | "name">,
  options?: {
    skipBootWait?: boolean;
    retryDelaysMs?: readonly number[];
    /** Legacy bulk sync must not resurrect user-deleted registry rows. */
    reactivateIfDeleted?: boolean;
  },
): Promise<void> {
  if (!isTeamverEmbedMode()) return;
  if (isTeamverProjectCollectionRouteSlug(project.id)) return;
  const tombstoneWorkspaceId = (await resolveActiveTeamverWorkspaceId())?.trim() ?? null;
  if (isTeamverProjectDeletedTombstoned(project.id, tombstoneWorkspaceId)) return;
  if (shouldSkipTeamverBffAuthCalls() || isDesignAuthRefreshDeclined()) {
    throw new TeamverProjectRegistryError("teamver_project_registry_unavailable");
  }
  if (!options?.skipBootWait) {
    await waitForEmbedBootIfNeeded();
  }

  const client = getDesignBffClient();
  if (!client) throw new TeamverProjectRegistryError("teamver_project_registry_unavailable");

  const workspaceId = (await resolveActiveTeamverWorkspaceId())?.trim();
  if (!workspaceId) throw new TeamverProjectRegistryError("teamver_workspace_required");

  const userId = await resolveRegistryUserId();

  const payload = buildTeamverProjectRegistryPayload(project, {
    reactivateIfDeleted: options?.reactivateIfDeleted === true,
  });
  const retryDelaysMs = options?.retryDelaysMs ?? REGISTRY_CREATE_RETRY_DELAYS_MS;

  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt += 1) {
    try {
      const registered = await withDesignBffCookieAuthRecovery(() =>
        client.http.post<TeamverRegisteredProject>(
          "/projects",
          payload,
          {
            workspaceId,
            ...TEAMVER_BFF_REQUEST_OPTIONS,
          },
        ),
      );
      invalidateRegisteredIdsCache();
      if (userId) primeFeAccessAllowed(project.id, workspaceId, userId);
      await rememberRegistryS3Prefix(project.id, workspaceId, registered);
      assertRegistryS3PrefixCached(workspaceId, project.id);
      return;
    } catch (err) {
      if (httpStatusOf(err) === 409) {
        if (options?.reactivateIfDeleted !== true && isRegistryProjectDeletedConflict(err)) {
          return;
        }
        invalidateRegisteredIdsCache();
        if (userId) primeFeAccessAllowed(project.id, workspaceId, userId);
        await rememberRegistryS3Prefix(project.id, workspaceId);
        assertRegistryS3PrefixCached(workspaceId, project.id);
        return;
      }
      const delayMs = retryDelaysMs[attempt];
      if (delayMs != null && isRetryableRegistryCreateError(err)) {
        await delay(delayMs);
        continue;
      }
      if (isTeamverBffUnauthorizedError(err)) {
        handleEmbedPassiveUnauthorized("bff");
        throw new TeamverProjectRegistryError("teamver_project_registry_unavailable");
      }
      devLog.warn("[teamver] project registry sync failed", err);
      throw new TeamverProjectRegistryError("teamver_project_registry_sync_failed");
    }
  }
}

function isRetryableRegistryCreateError(err: unknown): boolean {
  const status = httpStatusOf(err);
  // No status → treat as transport/unknown and allow a short retry.
  if (status == null) return true;
  // Auth / ACL / not-found must not burn the create retry budget.
  if (status === 401 || status === 403 || status === 404) return false;
  return status === 408 || status === 425 || status === 429 || status >= 500;
}

function isRegistryProjectDeletedConflict(err: unknown): boolean {
  if (httpStatusOf(err) !== 409) return false;
  const message = err instanceof Error ? err.message.toLowerCase() : "";
  return message.includes("project_deleted");
}

async function delay(ms: number): Promise<void> {
  if (ms <= 0) return;
  await new Promise((resolve) => setTimeout(resolve, ms));
}

/** Best-effort registry upsert for legacy daemon projects before access checks. */
export async function ensureTeamverProjectRegisteredById(projectId: string): Promise<void> {
  if (!isTeamverEmbedMode()) return;
  if (!legacyRegistryMigrationEnabled()) return;
  if (shouldSkipTeamverBffAuthCalls() || isDesignAuthRefreshDeclined()) return;

  const trimmedId = projectId.trim();
  if (!trimmedId) return;
  if (isTeamverProjectCollectionRouteSlug(trimmedId)) return;

  const project = (await fetchDaemonProjectsForRegistry()).find((row) => row.id === trimmedId);
  if (!project) return;

  await registerTeamverProjectIfNeeded(project, {
    skipBootWait: true,
    reactivateIfDeleted: false,
  });
}

/** Embed boot: upsert all daemon projects into design-api registry (legacy migration). */
export async function syncAllDaemonProjectsToRegistry(): Promise<void> {
  if (!isTeamverEmbedMode()) return;
  if (!legacyRegistryMigrationEnabled()) return;
  if (shouldSkipTeamverBffAuthCalls() || isDesignAuthRefreshDeclined()) return;
  // Called during embed boot before completeTeamverEmbedBoot — must not wait on boot gate.

  const client = getDesignBffClient();
  if (!client) return;

  const workspaceId = (await resolveActiveTeamverWorkspaceId())?.trim();
  if (!workspaceId) return;

  const now = Date.now();
  if (syncAllInflight) {
    await syncAllInflight;
  }
  if (
    syncAllWorkspaceId === workspaceId
    && now - syncAllAt < SYNC_ALL_MIN_INTERVAL_MS
  ) {
    return;
  }
  if (syncAllInflight) {
    await syncAllInflight;
    if (
      syncAllWorkspaceId === workspaceId
      && Date.now() - syncAllAt < SYNC_ALL_MIN_INTERVAL_MS
    ) {
      return;
    }
  }

  syncAllInflight = (async () => {
    const registeredIds = await listTeamverRegisteredProjectIds();
    const projects = await fetchDaemonProjectsForRegistry();
    const pending =
      registeredIds != null
        ? projects.filter((project) => !registeredIds.has(project.id))
        : projects;
    await Promise.all(
      pending.map((project) =>
        registerTeamverProjectIfNeeded(project, {
          skipBootWait: true,
          reactivateIfDeleted: false,
        }),
      ),
    );
    invalidateRegisteredIdsCache();
    syncAllWorkspaceId = workspaceId;
    syncAllAt = Date.now();
  })().finally(() => {
    syncAllInflight = null;
  });

  await syncAllInflight;
}


async function fetchRegistryProjectsFromBff(): Promise<{
  workspaceId: string;
  userId: string | null;
  projects: TeamverRegisteredProject[];
} | null> {
  if (!isTeamverEmbedMode()) return null;
  await waitForEmbedBootIfNeeded();

  const client = getDesignBffClient();
  if (!client) return null;

  const workspaceId = (await resolveActiveTeamverWorkspaceId())?.trim();
  if (!workspaceId) return null;

  // Sticky decline / skip used to return null before the workspace cache
  // lookup — home treated that as an empty rail. Serve last-good rows so a
  // cookie blip does not look like "no projects".
  const skipAuth = shouldSkipTeamverBffAuthCalls() || isDesignAuthRefreshDeclined();
  const userId = skipAuth ? null : await resolveRegistryUserId();
  const warm = readCachedRegistryList(workspaceId, userId);
  if (warm) return warm;
  if (skipAuth) {
    return readCachedRegistryList(workspaceId, userId, { ignoreTtl: true });
  }

  // Boot often races listTeamverRegistryProjects + listTeamverRegisteredProjectIds
  // before the 15s cache is warm — coalesce to one BFF GET.
  if (registryListInflight) return registryListInflight;

  registryListInflight = (async () => {
    try {
      const epochAtStart = registryListFetchEpoch;
      const result = await withDesignBffCookieAuthRecovery(() =>
        client.http.get<{ projects?: TeamverRegisteredProject[] }>(
          "/projects",
          {
            workspaceId,
            ...TEAMVER_BFF_REQUEST_OPTIONS,
          },
        ),
      );
      const projects = result.projects ?? [];
      const ids = new Set<string>();
      for (const project of projects) {
        const odProjectId = readRegistryOdProjectId(project);
        if (odProjectId) {
          ids.add(odProjectId);
          rememberTeamverProjectS3Prefix(workspaceId, odProjectId, project.s3Prefix);
        }
      }
      // Delete/unregister bumps epoch while this GET was in flight — never
      // re-seed the 15s cache with pre-delete membership.
      if (userId && epochAtStart === registryListFetchEpoch) {
        registryListCache = {
          workspaceId,
          userId,
          projects,
          ids,
          at: Date.now(),
        };
      }
      return { workspaceId, userId, projects };
    } catch (err) {
      if (isTeamverBffUnauthorizedError(err)) {
        handleEmbedPassiveUnauthorized("bff");
        return readCachedRegistryList(workspaceId, userId, { ignoreTtl: true });
      }
      devLog.warn("[teamver] project registry list failed", err);
      return null;
    } finally {
      registryListInflight = null;
    }
  })();

  return registryListInflight;
}

/** Embed list SSOT — full registry rows (RDS; safe across multi-node EC2). */
export async function listTeamverRegistryProjects(): Promise<TeamverRegisteredProject[] | null> {
  const fetched = await fetchRegistryProjectsFromBff();
  return fetched?.projects ?? null;
}

export async function listTeamverRegisteredProjectIds(): Promise<Set<string> | null> {
  if (!isTeamverEmbedMode()) return null;

  const workspaceId = (await resolveActiveTeamverWorkspaceId())?.trim();
  if (!workspaceId) return null;
  const userId = await resolveRegistryUserId();

  if (
    userId
    && registryListCache
    && registryListCache.workspaceId === workspaceId
    && registryListCache.userId === userId
    && Date.now() - registryListCache.at < REGISTRY_LIST_CACHE_MS
  ) {
    return registryListCache.ids;
  }

  const fetched = await fetchRegistryProjectsFromBff();
  if (!fetched) return null;

  if (userId && registryListCache?.workspaceId === workspaceId && registryListCache.userId === userId) {
    return registryListCache.ids;
  }

  const ids = new Set<string>();
  for (const project of fetched.projects) {
    const odProjectId = readRegistryOdProjectId(project);
    if (odProjectId) ids.add(odProjectId);
  }
  return ids;
}

export async function filterProjectsByTeamverRegistryIfNeeded<T extends Pick<Project, "id">>(
  projects: T[],
): Promise<T[]> {
  if (!isTeamverEmbedMode()) return projects;
  const fetched = await fetchRegistryProjectsFromBff();
  if (!fetched) {
    throw new TeamverProjectRegistryError("teamver_project_registry_list_failed");
  }
  const registeredIds = new Set<string>();
  const titlesById = new Map<string, string>();
  for (const row of fetched.projects) {
    const odProjectId = readRegistryOdProjectId(row);
    if (!odProjectId) continue;
    registeredIds.add(odProjectId);
    const title = row.title?.trim();
    if (title) titlesById.set(odProjectId, title);
  }
  return projects
    .filter((project) => registeredIds.has(project.id))
    .filter((project) => !isTeamverProjectDeletedTombstoned(project.id, fetched.workspaceId))
    .map((project) => {
      const title = titlesById.get(project.id);
      if (!title || !("name" in project)) return project;
      const currentName = (project as Pick<Project, "id" | "name">).name;
      const name = resolveProjectDisplayName({ id: project.id, name: currentName }, title);
      if (name === currentName) return project;
      return { ...project, name } as T;
    });
}

/** Embed: single registry row (od id or DPRJ id). */
export async function fetchTeamverProject(
  projectRef: string,
): Promise<TeamverRegisteredProject | null> {
  const outcome = await fetchTeamverProjectAccessOutcome(projectRef);
  return outcome.status === "found" ? outcome.project : null;
}

type TeamverProjectAccessOutcome =
  | { status: "found"; project: TeamverRegisteredProject }
  | { status: "denied" }
  | { status: "unavailable" };

async function fetchTeamverProjectAccessOutcome(
  projectRef: string,
): Promise<TeamverProjectAccessOutcome> {
  if (!isTeamverEmbedMode()) return { status: "unavailable" };
  await waitForEmbedBootIfNeeded();

  const trimmedRef = projectRef.trim();
  if (!trimmedRef) return { status: "denied" };
  if (isTeamverProjectCollectionRouteSlug(trimmedRef)) return { status: "denied" };

  // Dead cookie / soft+hard sticky: C1 owns recovery. Hitting /projects here
  // re-triggers withDesignBffCookieAuthRecovery → 401 spam (soft used to stay open
  // for S3 prefix; cache miss now fails soft until sticky clears).
  if (shouldSkipTeamverBffAuthCalls() || isDesignAuthRefreshDeclined()) {
    return { status: "unavailable" };
  }

  const client = getDesignBffClient();
  if (!client) return { status: "unavailable" };

  try {
    const workspaceId = await resolveWorkspaceIdForProjectAccess();
    if (!workspaceId?.trim()) return { status: "unavailable" };

    const project = await withDesignBffCookieAuthRecovery(() =>
      client.http.get<TeamverRegisteredProject>(
        `/projects/${encodeURIComponent(trimmedRef)}`,
        {
          workspaceId: workspaceId.trim(),
          ...TEAMVER_BFF_REQUEST_OPTIONS,
        },
      ),
    );
    return { status: "found", project };
  } catch (err) {
    const status = httpStatusOf(err);
    // SDK: 403→ForbiddenError, 404→NotFoundError. workspace_mismatch /
    // project_owner_mismatch are expected ACL denies — not console.warn noise.
    if (status === 403 || status === 404) {
      return { status: "denied" };
    }
    if (status === 401) {
      handleEmbedPassiveUnauthorized("bff");
      return { status: "unavailable" };
    }
    devLog.warn("[teamver] project fetch failed", err);
    return { status: "unavailable" };
  }
}

async function isProjectRegisteredInWorkspace(
  projectId: string,
  workspaceId: string,
): Promise<boolean | null> {
  const registeredIds = await getRegisteredProjectIds(workspaceId);
  if (registeredIds === null) return null;
  return registeredIds.has(projectId);
}

function isDirectRegistryProjectHit(
  projectId: string,
  project: TeamverRegisteredProject | null,
): project is TeamverRegisteredProject {
  if (!project) return false;
  const odProjectId = readRegistryOdProjectId(project);
  if (odProjectId) return odProjectId === projectId;
  return Boolean(project.s3Prefix?.trim());
}

async function resolveWorkspaceIdForProjectAccess(): Promise<string | null> {
  const fromSession = (await resolveActiveTeamverWorkspaceId())?.trim();
  if (fromSession) return fromSession;
  const client = getDesignBffClient();
  return (await client?.workspaceStore?.get())?.trim() || null;
}

async function checkTeamverProjectAccessOnce(
  trimmedId: string,
  workspaceId: string,
  cacheKey: string | null,
): Promise<ProjectAccessCheckResult> {
  if (cacheKey) {
    const cached = feAccessCache.get(cacheKey);
    if (cached?.allowed && Date.now() - cached.at < FE_ACCESS_CACHE_MS) {
      return "allowed";
    }
  }

  let allowed = await isProjectRegisteredInWorkspace(trimmedId, workspaceId);
  if (allowed === true) {
    if (cacheKey) feAccessCache.set(cacheKey, { allowed: true, at: Date.now() });
    return "allowed";
  }

  if (legacyRegistryMigrationEnabled()) {
    await ensureTeamverProjectRegisteredById(trimmedId);
    allowed = await isProjectRegisteredInWorkspace(trimmedId, workspaceId);
    if (allowed === true) {
      if (cacheKey) feAccessCache.set(cacheKey, { allowed: true, at: Date.now() });
      return "allowed";
    }
  }

  // The list endpoint can be briefly stale/partial during auth refresh,
  // workspace switch, or registry write propagation. Before showing the
  // destructive "not accessible in this workspace" UX, confirm against the
  // single-project registry endpoint. This endpoint is the authoritative
  // membership check for deep-linked/detail routes and also refreshes the
  // S3 prefix cache when it succeeds.
  const direct = await fetchTeamverProjectAccessOutcome(trimmedId);
  if (direct.status === "found" && isDirectRegistryProjectHit(trimmedId, direct.project)) {
    const userId = await resolveRegistryUserId();
    if (userId) primeFeAccessAllowed(trimmedId, workspaceId, userId);
    await rememberRegistryS3Prefix(trimmedId, workspaceId, direct.project);
    return "allowed";
  }
  if (direct.status === "denied") return "denied";
  if (allowed === false) return "transient";
  return "transient";
}

/** Embed: registry membership gate — daemon middleware owns `/access` + S3 prefix. */
export async function assertTeamverProjectAccessIfNeeded(
  projectId: string,
): Promise<boolean> {
  if (!isTeamverEmbedMode()) return true;
  await waitForEmbedBootIfNeeded();

  const trimmedId = projectId.trim();
  if (!trimmedId) return false;
  if (isTeamverProjectCollectionRouteSlug(trimmedId)) return false;

  const client = getDesignBffClient();
  if (!client) return false;

  const workspaceId = (await resolveWorkspaceIdForProjectAccess())?.trim();
  if (!workspaceId) return false;

  const userId = await resolveRegistryUserId();
  const cacheKey = userId
    ? feAccessCacheKey(workspaceId, trimmedId, userId)
    : null;

  for (let attempt = 0; attempt < PROJECT_ACCESS_RETRY_DELAYS_MS.length; attempt += 1) {
    const delayMs = PROJECT_ACCESS_RETRY_DELAYS_MS[attempt] ?? 0;
    if (delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    const result = await checkTeamverProjectAccessOnce(trimmedId, workspaceId, cacheKey);
    if (result !== "transient") {
      return result === "allowed";
    }
  }

  return false;
}

/** Embed: daemon delete then registry soft-delete (both required). */
export async function unregisterTeamverProjectFromRegistryIfNeeded(
  projectId: string,
): Promise<boolean> {
  if (!isTeamverEmbedMode()) return true;

  const trimmedId = projectId.trim();
  if (!trimmedId) return true;
  if (shouldSkipTeamverBffAuthCalls() || isDesignAuthRefreshDeclined()) return false;

  const client = getDesignBffClient();
  if (!client) return false;

  let workspaceId: string | null = null;
  try {
    workspaceId = (await resolveActiveTeamverWorkspaceId())?.trim() ?? null;
    if (!workspaceId) return false;

    await withDesignBffCookieAuthRecovery(() =>
      client.http.delete<void>(
        `/projects/${encodeURIComponent(trimmedId)}`,
        {
          workspaceId,
          ...TEAMVER_BFF_REQUEST_OPTIONS,
        },
      ),
    );
    invalidateRegisteredIdsCache();
    invalidateFeAccessCache(trimmedId, workspaceId);
    return true;
  } catch (err) {
    if (httpStatusOf(err) === 404) {
      invalidateRegisteredIdsCache();
      if (workspaceId) invalidateFeAccessCache(trimmedId, workspaceId);
      return true;
    }
    if (isTeamverBffUnauthorizedError(err)) {
      handleEmbedPassiveUnauthorized("bff");
      return false;
    }
    devLog.warn("[teamver] project registry delete failed", err);
    return false;
  }
}

/** Best-effort registry DELETE retries after a tombstoned UI delete (auth blips). */
export function scheduleTeamverRegistryDeleteRetry(projectId: string): void {
  if (typeof window === "undefined" || !isTeamverEmbedMode()) return;
  const trimmedId = projectId.trim();
  if (!trimmedId) return;
  for (const delayMs of [1500, 5000, 20_000, 60_000]) {
    window.setTimeout(() => {
      void unregisterTeamverProjectFromRegistryIfNeeded(trimmedId);
    }, delayMs);
  }
}
